import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { 
  ArrowLeft,
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  ExternalLink, 
  FileText, 
  Info,
  Maximize, 
  Minimize,
  Play, 
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Key takeaway component for highlighting important points
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative bg-gradient-to-r from-green-50 via-blue-50 to-slate-50 border border-green-200 rounded-lg p-3 my-4 overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-500 to-blue-600"></div>
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-5 h-5 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-2.5 w-2.5 text-white" />
          </div>
        </div>
        <div className="ml-3 text-xs text-slate-700">{children}</div>
      </div>
    </div>
  );
};

// SVG Diagrams
const SensingBalanceDiagram = () => (
  <svg viewBox="0 0 600 300" className="w-full h-auto">
    {/* Balance scale base */}
    <line x1="300" y1="250" x2="300" y2="150" stroke="#374151" strokeWidth="3"/>
    <path d="M 250 250 L 350 250 L 340 260 L 260 260 Z" fill="#374151"/>
    
    {/* Balance beam */}
    <line x1="150" y1="150" x2="450" y2="150" stroke="#374151" strokeWidth="3"/>
    <circle cx="300" cy="150" r="8" fill="#374151"/>
    
    {/* Left side - Nutrient Abundance */}
    <line x1="150" y1="150" x2="150" y2="100" stroke="#374151" strokeWidth="2"/>
    <rect x="80" y="80" width="140" height="80" rx="8" fill="#fee2e2" stroke="#ef4444" strokeWidth="2"/>
    <text x="150" y="100" textAnchor="middle" className="fill-red-800 text-xs font-semibold">
      Nutrient Abundance
    </text>
    <text x="150" y="120" textAnchor="middle" className="fill-red-700 text-xs">
      IIS ↑
    </text>
    <text x="150" y="135" textAnchor="middle" className="fill-red-700 text-xs">
      mTOR ↑
    </text>
    <text x="150" y="155" textAnchor="middle" className="fill-red-600 text-xs">
      (growth, storage)
    </text>
    
    {/* Right side - Nutrient Scarcity */}
    <line x1="450" y1="150" x2="450" y2="100" stroke="#374151" strokeWidth="2"/>
    <rect x="380" y="80" width="140" height="80" rx="8" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
    <text x="450" y="100" textAnchor="middle" className="fill-green-800 text-xs font-semibold">
      Nutrient Scarcity/Stress
    </text>
    <text x="450" y="120" textAnchor="middle" className="fill-green-700 text-xs">
      AMPK ↑
    </text>
    <text x="450" y="135" textAnchor="middle" className="fill-green-700 text-xs">
      Sirtuins ↑
    </text>
    <text x="450" y="155" textAnchor="middle" className="fill-green-600 text-xs">
      (maintenance, repair, longevity)
    </text>
  </svg>
);

const HormeticResponseCurveDiagram = () => (
  <svg viewBox="0 0 600 300" className="w-full h-auto">
    {/* Axes */}
    <line x1="50" y1="250" x2="550" y2="250" stroke="#374151" strokeWidth="2"/>
    <line x1="50" y1="250" x2="50" y2="50" stroke="#374151" strokeWidth="2"/>
    
    {/* X-axis label */}
    <text x="300" y="290" textAnchor="middle" className="fill-slate-700 text-xs font-semibold">
      Stressor Intensity
    </text>
    
    {/* Y-axis label */}
    <text x="25" y="150" textAnchor="middle" className="fill-slate-700 text-xs font-semibold" transform="rotate(-90 25 150)">
      Health Benefit
    </text>
    
    {/* Hormetic curve */}
    <path 
      d="M 50 250 Q 150 200, 200 150 T 300 120 Q 350 110, 380 120 Q 420 130, 450 180 T 500 250" 
      fill="none" 
      stroke="#3b82f6" 
      strokeWidth="3"
    />
    
    {/* Zones */}
    {/* Insufficient zone */}
    <rect x="50" y="150" width="100" height="100" fill="#fef3c7" opacity="0.3"/>
    <text x="100" y="180" textAnchor="middle" className="fill-yellow-700 text-xs font-semibold">
      Insufficient
    </text>
    <text x="100" y="195" textAnchor="middle" className="fill-yellow-600 text-xs">
      No stimulus
    </text>
    
    {/* Optimal zone */}
    <rect x="150" y="120" width="180" height="130" fill="#d1fae5" opacity="0.3"/>
    <text x="240" y="140" textAnchor="middle" className="fill-green-700 text-xs font-semibold">
      Optimal Zone
    </text>
    <text x="240" y="155" textAnchor="middle" className="fill-green-600 text-xs">
      "Sweet spot"
    </text>
    <text x="240" y="170" textAnchor="middle" className="fill-green-600 text-xs">
      Maximum benefit
    </text>
    
    {/* Toxic zone */}
    <rect x="330" y="120" width="170" height="130" fill="#fee2e2" opacity="0.3"/>
    <text x="415" y="180" textAnchor="middle" className="fill-red-700 text-xs font-semibold">
      Toxic/Harmful
    </text>
    <text x="415" y="195" textAnchor="middle" className="fill-red-600 text-xs">
      Excessive stress
    </text>
    
    {/* Peak benefit marker */}
    <circle cx="300" cy="120" r="5" fill="#22c55e"/>
    <text x="300" y="105" textAnchor="middle" className="fill-green-700 text-xs font-semibold">
      Peak Benefit
    </text>
  </svg>
);

const InflammationPathwayDiagram = () => (
  <svg viewBox="0 0 600 350" className="w-full h-auto">
    {/* Central inflammation node */}
    <circle cx="300" cy="175" r="60" fill="#fee2e2" stroke="#ef4444" strokeWidth="2"/>
    <text x="300" y="170" textAnchor="middle" className="fill-red-800 text-xs font-semibold">
      Chronic
    </text>
    <text x="300" y="185" textAnchor="middle" className="fill-red-800 text-xs font-semibold">
      Inflammation
    </text>
    
    {/* Drivers (inputs) */}
    {/* Top left - Poor Diet */}
    <rect x="50" y="40" width="120" height="50" rx="8" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2"/>
    <text x="110" y="60" textAnchor="middle" className="fill-amber-800 text-xs font-semibold">
      Poor Diet
    </text>
    <text x="110" y="75" textAnchor="middle" className="fill-amber-700 text-xs">
      (processed foods)
    </text>
    <line x1="170" y1="80" x2="245" y2="140" stroke="#f59e0b" strokeWidth="2" markerEnd="url(#arrowhead)"/>
    
    {/* Top right - Sedentary */}
    <rect x="430" y="40" width="120" height="50" rx="8" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2"/>
    <text x="490" y="60" textAnchor="middle" className="fill-amber-800 text-xs font-semibold">
      Sedentary
    </text>
    <text x="490" y="75" textAnchor="middle" className="fill-amber-700 text-xs">
      Lifestyle
    </text>
    <line x1="430" y1="80" x2="355" y2="140" stroke="#f59e0b" strokeWidth="2" markerEnd="url(#arrowhead)"/>
    
    {/* Bottom left - Poor Sleep */}
    <rect x="50" y="260" width="120" height="50" rx="8" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2"/>
    <text x="110" y="280" textAnchor="middle" className="fill-amber-800 text-xs font-semibold">
      Poor Sleep
    </text>
    <text x="110" y="295" textAnchor="middle" className="fill-amber-700 text-xs">
      (&lt; 7 hrs)
    </text>
    <line x1="170" y1="270" x2="245" y2="210" stroke="#f59e0b" strokeWidth="2" markerEnd="url(#arrowhead)"/>
    
    {/* Bottom right - Chronic Stress */}
    <rect x="430" y="260" width="120" height="50" rx="8" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2"/>
    <text x="490" y="280" textAnchor="middle" className="fill-amber-800 text-xs font-semibold">
      Chronic Stress
    </text>
    <text x="490" y="295" textAnchor="middle" className="fill-amber-700 text-xs">
      (psychological)
    </text>
    <line x1="430" y1="270" x2="355" y2="210" stroke="#f59e0b" strokeWidth="2" markerEnd="url(#arrowhead)"/>
    
    {/* Consequences (outputs) */}
    <text x="300" y="30" textAnchor="middle" className="fill-slate-700 text-xs font-semibold">
      Drivers
    </text>
    <text x="300" y="330" textAnchor="middle" className="fill-slate-700 text-xs font-semibold">
      Consequences: CVD, Cancer, Neurodegeneration, Metabolic Disease
    </text>
    
    {/* Arrow marker definition */}
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#f59e0b"/>
      </marker>
    </defs>
  </svg>
);

const GenomicMaintenanceDiagram = () => (
  <svg viewBox="0 0 600 400" className="w-full h-auto">
    {/* Central DNA helix */}
    <g transform="translate(300, 200)">
      {/* DNA double helix */}
      <path 
        d="M -30 -80 Q -20 -60, -10 -40 T 0 0 T 10 40 T 20 60 T 30 80"
        fill="none" 
        stroke="#3b82f6" 
        strokeWidth="3"
      />
      <path 
        d="M 30 -80 Q 20 -60, 10 -40 T 0 0 T -10 40 T -20 60 T -30 80"
        fill="none" 
        stroke="#3b82f6" 
        strokeWidth="3"
      />
      
      {/* Connecting bars */}
      <line x1="-25" y1="-70" x2="25" y2="-70" stroke="#60a5fa" strokeWidth="2"/>
      <line x1="-20" y1="-50" x2="20" y2="-50" stroke="#60a5fa" strokeWidth="2"/>
      <line x1="-15" y1="-30" x2="15" y2="-30" stroke="#60a5fa" strokeWidth="2"/>
      <line x1="-10" y1="-10" x2="10" y2="-10" stroke="#60a5fa" strokeWidth="2"/>
      <line x1="-10" y1="10" x2="10" y2="10" stroke="#60a5fa" strokeWidth="2"/>
      <line x1="-15" y1="30" x2="15" y2="30" stroke="#60a5fa" strokeWidth="2"/>
      <line x1="-20" y1="50" x2="20" y2="50" stroke="#60a5fa" strokeWidth="2"/>
      <line x1="-25" y1="70" x2="25" y2="70" stroke="#60a5fa" strokeWidth="2"/>
    </g>
    
    {/* Damage sources */}
    <text x="300" y="30" textAnchor="middle" className="fill-slate-700 text-xs font-semibold">
      DNA Damage Sources
    </text>
    
    {/* Left side - Internal */}
    <rect x="50" y="80" width="140" height="60" rx="8" fill="#fee2e2" stroke="#ef4444" strokeWidth="2"/>
    <text x="120" y="100" textAnchor="middle" className="fill-red-800 text-xs font-semibold">
      Internal Damage
    </text>
    <text x="120" y="115" textAnchor="middle" className="fill-red-700 text-xs">
      • ROS
    </text>
    <text x="120" y="130" textAnchor="middle" className="fill-red-700 text-xs">
      • Replication errors
    </text>
    <line x1="190" y1="110" x2="260" y2="170" stroke="#ef4444" strokeWidth="2" markerEnd="url(#arrowhead2)"/>
    
    {/* Right side - External */}
    <rect x="410" y="80" width="140" height="60" rx="8" fill="#fee2e2" stroke="#ef4444" strokeWidth="2"/>
    <text x="480" y="100" textAnchor="middle" className="fill-red-800 text-xs font-semibold">
      External Damage
    </text>
    <text x="480" y="115" textAnchor="middle" className="fill-red-700 text-xs">
      • UV radiation
    </text>
    <text x="480" y="130" textAnchor="middle" className="fill-red-700 text-xs">
      • Toxins
    </text>
    <line x1="410" y1="110" x2="340" y2="170" stroke="#ef4444" strokeWidth="2" markerEnd="url(#arrowhead2)"/>
    
    {/* Repair mechanisms */}
    <text x="300" y="320" textAnchor="middle" className="fill-slate-700 text-xs font-semibold">
      Repair Mechanisms
    </text>
    
    {/* Bottom repair boxes */}
    <rect x="30" y="340" width="120" height="50" rx="8" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
    <text x="90" y="360" textAnchor="middle" className="fill-green-800 text-xs font-semibold">
      Base Excision
    </text>
    <text x="90" y="375" textAnchor="middle" className="fill-green-700 text-xs">
      Repair (BER)
    </text>
    
    <rect x="170" y="340" width="120" height="50" rx="8" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
    <text x="230" y="360" textAnchor="middle" className="fill-green-800 text-xs font-semibold">
      Nucleotide
    </text>
    <text x="230" y="375" textAnchor="middle" className="fill-green-700 text-xs">
      Excision (NER)
    </text>
    
    <rect x="310" y="340" width="120" height="50" rx="8" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
    <text x="370" y="360" textAnchor="middle" className="fill-green-800 text-xs font-semibold">
      Double-Strand
    </text>
    <text x="370" y="375" textAnchor="middle" className="fill-green-700 text-xs">
      Break Repair
    </text>
    
    <rect x="450" y="340" width="120" height="50" rx="8" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
    <text x="510" y="360" textAnchor="middle" className="fill-green-800 text-xs font-semibold">
      Mismatch
    </text>
    <text x="510" y="375" textAnchor="middle" className="fill-green-700 text-xs">
      Repair (MMR)
    </text>
    
    {/* Arrow marker definition */}
    <defs>
      <marker id="arrowhead2" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#ef4444"/>
      </marker>
    </defs>
  </svg>
);

const CirculatoryHealthDiagram = () => (
  <svg viewBox="0 0 600 400" className="w-full h-auto">
    {/* Heart in center */}
    <g transform="translate(300, 200)">
      <path 
        d="M 0,-20 C -10,-30 -30,-30 -30,-15 C -30,0 -15,15 0,30 C 15,15 30,0 30,-15 C 30,-30 10,-30 0,-20 Z"
        fill="#ef4444" 
        stroke="#dc2626" 
        strokeWidth="2"
      />
      <text x="0" y="5" textAnchor="middle" className="fill-white text-xs font-semibold">
        Heart
      </text>
    </g>
    
    {/* Arteries */}
    <text x="300" y="30" textAnchor="middle" className="fill-slate-700 text-xs font-semibold">
      Circulatory System Health
    </text>
    
    {/* Brain circulation */}
    <circle cx="300" cy="80" r="30" fill="#f0f9ff" stroke="#3b82f6" strokeWidth="2"/>
    <text x="300" y="85" textAnchor="middle" className="fill-blue-800 text-xs font-semibold">
      Brain
    </text>
    <path d="M 300,110 Q 300,140 300,170" fill="none" stroke="#3b82f6" strokeWidth="3"/>
    <path d="M 300,230 Q 300,260 300,290" fill="none" stroke="#3b82f6" strokeWidth="3"/>
    
    {/* Kidneys */}
    <ellipse cx="200" cy="200" rx="25" ry="35" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2"/>
    <text x="200" y="205" textAnchor="middle" className="fill-amber-800 text-xs">
      Kidney
    </text>
    <ellipse cx="400" cy="200" rx="25" ry="35" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2"/>
    <text x="400" y="205" textAnchor="middle" className="fill-amber-800 text-xs">
      Kidney
    </text>
    <path d="M 270,200 L 225,200" stroke="#3b82f6" strokeWidth="3"/>
    <path d="M 330,200 L 375,200" stroke="#3b82f6" strokeWidth="3"/>
    
    {/* Muscles */}
    <rect x="250" y="320" width="100" height="40" rx="20" fill="#ede9fe" stroke="#8b5cf6" strokeWidth="2"/>
    <text x="300" y="345" textAnchor="middle" className="fill-purple-800 text-xs font-semibold">
      Muscles
    </text>
    
    {/* Health factors */}
    <g transform="translate(100, 100)">
      <rect x="-50" y="-20" width="100" height="40" rx="8" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
      <text x="0" y="-5" textAnchor="middle" className="fill-green-800 text-xs font-semibold">
        Endothelial
      </text>
      <text x="0" y="10" textAnchor="middle" className="fill-green-800 text-xs">
        Function
      </text>
    </g>
    
    <g transform="translate(500, 100)">
      <rect x="-50" y="-20" width="100" height="40" rx="8" fill="#fee2e2" stroke="#ef4444" strokeWidth="2"/>
      <text x="0" y="-5" textAnchor="middle" className="fill-red-800 text-xs font-semibold">
        Blood
      </text>
      <text x="0" y="10" textAnchor="middle" className="fill-red-800 text-xs">
        Pressure
      </text>
    </g>
    
    <g transform="translate(100, 300)">
      <rect x="-50" y="-20" width="100" height="40" rx="8" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
      <text x="0" y="-5" textAnchor="middle" className="fill-indigo-800 text-xs font-semibold">
        Lipid
      </text>
      <text x="0" y="10" textAnchor="middle" className="fill-indigo-800 text-xs">
        Profile
      </text>
    </g>
    
    <g transform="translate(500, 300)">
      <rect x="-50" y="-20" width="100" height="40" rx="8" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2"/>
      <text x="0" y="-5" textAnchor="middle" className="fill-amber-800 text-xs font-semibold">
        Blood
      </text>
      <text x="0" y="10" textAnchor="middle" className="fill-amber-800 text-xs">
        Flow
      </text>
    </g>
  </svg>
);

const slides = [
  // Slide 1: Title Slide
  {
    id: 1,
    title: "Lifestyle Interventions for Longevity and Health Optimization",
    content: (
      <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-lg p-8">
        <h1 className="text-2xl md:text-3xl font-bold text-center mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          Lifestyle Interventions for Longevity and Health Optimization:
        </h1>
        <h2 className="text-xl md:text-2xl font-semibold text-center mb-6 text-gray-800">
          A SHIFT Framework Approach
        </h2>
        <p className="text-sm text-gray-600 text-center mb-2">Practical Applications for Clinical Practice</p>
        <p className="text-xs text-gray-500 text-center">Next Generation Medicine</p>
      </div>
    )
  },
  
  // Slide 2: The Paradigm Shift in Modern Medicine
  {
    id: 2,
    title: "Beyond Disease Treatment: The Rise of Longevity Medicine",
    content: (
      <>
        <div className="mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-2">Traditional Medicine</h3>
              <p className="text-sm text-red-700">Primarily reactive, focused on treating established diseases</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Longevity Medicine</h3>
              <p className="text-sm text-green-700">Proactive, aiming to extend healthspan and optimize overall longevity</p>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <svg viewBox="0 0 600 200" className="w-full h-auto">
            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#6366f1" />
              </marker>
            </defs>
            
            <rect x="50" y="50" width="200" height="100" rx="8" fill="#fee2e2" stroke="#f87171" strokeWidth="2"/>
            <text x="150" y="90" textAnchor="middle" className="fill-red-800 text-sm font-semibold">
              Reactive
            </text>
            <text x="150" y="110" textAnchor="middle" className="fill-red-700 text-xs">
              Disease Treatment
            </text>
            
            <line x1="260" y1="100" x2="340" y2="100" stroke="#6366f1" strokeWidth="2" markerEnd="url(#arrowhead)"/>
            
            <rect x="350" y="50" width="200" height="100" rx="8" fill="#dcfce7" stroke="#4ade80" strokeWidth="2"/>
            <text x="450" y="90" textAnchor="middle" className="fill-green-800 text-sm font-semibold">
              Proactive
            </text>
            <text x="450" y="110" textAnchor="middle" className="fill-green-700 text-xs">
              Healthspan Optimization
            </text>
          </svg>
        </div>
        
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            <strong>Addressing the global burden of age-related chronic diseases:</strong>
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-50 p-3 rounded text-center">
              <div className="text-lg mb-1">🫀</div>
              <p className="text-xs text-gray-700">Cardiovascular Disease</p>
            </div>
            <div className="bg-gray-50 p-3 rounded text-center">
              <div className="text-lg mb-1">🩸</div>
              <p className="text-xs text-gray-700">Type 2 Diabetes</p>
            </div>
            <div className="bg-gray-50 p-3 rounded text-center">
              <div className="text-lg mb-1">🧠</div>
              <p className="text-xs text-gray-700">Neurodegenerative</p>
            </div>
            <div className="bg-gray-50 p-3 rounded text-center">
              <div className="text-lg mb-1">🎗️</div>
              <p className="text-xs text-gray-700">Cancer</p>
            </div>
          </div>
        </div>
        
        <KeyTakeaway>
          Goal: Enhance vitality and functional capacity throughout life, not just increase lifespan.
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 3: Core Pillars of Lifestyle Intervention
  {
    id: 3,
    title: "Foundational Lifestyle Interventions: The Cornerstones of Healthspan",
    content: (
      <>
        <div className="mb-6">
          <p className="text-sm text-gray-700 mb-4">
            Nutrition, Exercise/Movement, and Sleep are primary determinants of:
          </p>
          <ul className="space-y-2 text-sm text-gray-700 mb-6">
            <li className="flex items-center">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2" />
              Cellular function
            </li>
            <li className="flex items-center">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2" />
              Metabolic regulation
            </li>
            <li className="flex items-center">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2" />
              Hormonal balance
            </li>
            <li className="flex items-center">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2" />
              Resilience against age-related decline
            </li>
          </ul>
        </div>
        
        <div className="flex justify-center mb-6">
          <svg viewBox="0 0 400 400" className="w-full max-w-md h-auto">
            {/* Three interlocking circles */}
            <circle cx="150" cy="150" r="100" fill="#e0f2fe" stroke="#0ea5e9" strokeWidth="3" opacity="0.8"/>
            <circle cx="250" cy="150" r="100" fill="#dcfce7" stroke="#22c55e" strokeWidth="3" opacity="0.8"/>
            <circle cx="200" cy="230" r="100" fill="#f3e8ff" stroke="#a855f7" strokeWidth="3" opacity="0.8"/>
            
            {/* Labels */}
            <text x="120" y="120" textAnchor="middle" className="fill-blue-800 text-sm font-semibold">
              Nutrition
            </text>
            <text x="280" y="120" textAnchor="middle" className="fill-green-800 text-sm font-semibold">
              Exercise/
            </text>
            <text x="280" y="135" textAnchor="middle" className="fill-green-800 text-sm font-semibold">
              Movement
            </text>
            <text x="200" y="280" textAnchor="middle" className="fill-purple-800 text-sm font-semibold">
              Sleep
            </text>
            
            {/* Center text */}
            <text x="200" y="180" textAnchor="middle" className="fill-gray-800 text-xs font-bold">
              Healthspan &
            </text>
            <text x="200" y="195" textAnchor="middle" className="fill-gray-800 text-xs font-bold">
              Longevity
            </text>
          </svg>
        </div>
        
        <KeyTakeaway>
          These habits are interconnected and profoundly impact health trajectory.
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 4: Introducing the SHIFT Framework
  {
    id: 4,
    title: "The SHIFT Framework: Understanding Lifestyle's Impact",
    content: (
      <>
        <div className="mb-4">
          <p className="text-sm text-gray-700 mb-3">
            A scaffold to examine how nutrition, exercise, and sleep modulate aging and healthspan:
          </p>
          <ul className="space-y-1 text-sm text-gray-700 mb-6">
            <li><strong className="text-blue-700">S</strong>ensing & Metabolic Fuel-Gauging</li>
            <li><strong className="text-green-700">H</strong>omeostatic Defence & Repair</li>
            <li><strong className="text-red-700">I</strong>nflammation & Immune Tone</li>
            <li><strong className="text-purple-700">F</strong>ibrosis / Structural Remodeling</li>
            <li><strong className="text-orange-700">T</strong>imekeeping & Chronobiology</li>
          </ul>
        </div>
        
        <div className="flex justify-center mb-6">
          <svg viewBox="0 0 500 500" className="w-full max-w-lg h-auto">
            {/* Central circle */}
            <circle cx="250" cy="250" r="80" fill="#f3f4f6" stroke="#6b7280" strokeWidth="2"/>
            <text x="250" y="245" textAnchor="middle" className="fill-gray-800 text-sm font-semibold">
              Lifestyle
            </text>
            <text x="250" y="265" textAnchor="middle" className="fill-gray-800 text-sm font-semibold">
              Interventions
            </text>
            
            {/* Pentagon points */}
            <g transform="translate(250,250)">
              {/* S - Sensing */}
              <g transform="translate(0,-150)">
                <circle r="70" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2"/>
                <text y="-5" textAnchor="middle" className="fill-blue-800 text-sm font-bold">S</text>
                <text y="10" textAnchor="middle" className="fill-blue-700 text-xs">Nutrient sensing</text>
                <text y="23" textAnchor="middle" className="fill-blue-600 text-xs">(Insulin/IGF-1,</text>
                <text y="36" textAnchor="middle" className="fill-blue-600 text-xs">mTOR, AMPK,</text>
                <text y="49" textAnchor="middle" className="fill-blue-600 text-xs">Sirtuins)</text>
              </g>
              
              {/* H - Homeostatic */}
              <g transform="translate(142.7,-46.4)">
                <circle r="70" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
                <text y="-5" textAnchor="middle" className="fill-green-800 text-sm font-bold">H</text>
                <text y="10" textAnchor="middle" className="fill-green-700 text-xs">Damage protection</text>
                <text y="23" textAnchor="middle" className="fill-green-600 text-xs">& renewal</text>
                <text y="36" textAnchor="middle" className="fill-green-600 text-xs">(NRF2, p53/p16,</text>
                <text y="49" textAnchor="middle" className="fill-green-600 text-xs">Autophagy)</text>
              </g>
              
              {/* I - Inflammation */}
              <g transform="translate(88.2,121.4)">
                <circle r="70" fill="#fee2e2" stroke="#ef4444" strokeWidth="2"/>
                <text y="-5" textAnchor="middle" className="fill-red-800 text-sm font-bold">I</text>
                <text y="10" textAnchor="middle" className="fill-red-700 text-xs">Inflammatory &</text>
                <text y="23" textAnchor="middle" className="fill-red-700 text-xs">immune regulation</text>
                <text y="36" textAnchor="middle" className="fill-red-600 text-xs">(NF-κB, JAK-STAT,</text>
                <text y="49" textAnchor="middle" className="fill-red-600 text-xs">SASP)</text>
              </g>
              
              {/* F - Fibrosis */}
              <g transform="translate(-88.2,121.4)">
                <circle r="70" fill="#f3e8ff" stroke="#a855f7" strokeWidth="2"/>
                <text y="-5" textAnchor="middle" className="fill-purple-800 text-sm font-bold">F</text>
                <text y="10" textAnchor="middle" className="fill-purple-700 text-xs">ECM & tissue</text>
                <text y="23" textAnchor="middle" className="fill-purple-700 text-xs">integrity</text>
                <text y="36" textAnchor="middle" className="fill-purple-600 text-xs">(TGF-β, WNT,</text>
                <text y="49" textAnchor="middle" className="fill-purple-600 text-xs">ECM Cross-linking)</text>
              </g>
              
              {/* T - Timekeeping */}
              <g transform="translate(-142.7,-46.4)">
                <circle r="70" fill="#fed7aa" stroke="#f97316" strokeWidth="2"/>
                <text y="-5" textAnchor="middle" className="fill-orange-800 text-sm font-bold">T</text>
                <text y="10" textAnchor="middle" className="fill-orange-700 text-xs">Biological rhythms</text>
                <text y="23" textAnchor="middle" className="fill-orange-700 text-xs">& aging clocks</text>
                <text y="36" textAnchor="middle" className="fill-orange-600 text-xs">(CLOCK/BMAL1,</text>
                <text y="49" textAnchor="middle" className="fill-orange-600 text-xs">Epigenetic Clocks)</text>
              </g>
            </g>
          </svg>
        </div>
        
        <p className="text-sm text-gray-700 text-center italic">
          This presentation will analyze lifestyle interventions through these five pillars.
        </p>
      </>
    )
  },
  
  // Slide 5: Lecture Goals for Clinicians
  {
    id: 5,
    title: "What You Will Gain From This Session",
    content: (
      <>
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Evidence-Based Understanding</h3>
              <p className="text-sm text-gray-600">
                An evidence-saturated, digestible understanding of how to leverage lifestyle interventions.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Mechanistic Insights</h3>
              <p className="text-sm text-gray-600">
                Insight into the mechanisms by which nutrition, exercise, and sleep promote healthspan.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-purple-600" />
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Actionable Strategies</h3>
              <p className="text-sm text-gray-600">
                Actionable strategies and practical protocols for patient care.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-orange-600" />
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Key Biomarkers</h3>
              <p className="text-sm text-gray-600">
                Key biomarkers to consider for monitoring interventions.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800 text-center font-medium">
            By the end of this session, you'll have a comprehensive toolkit for implementing 
            lifestyle interventions through the SHIFT framework lens.
          </p>
        </div>
      </>
    )
  },
  
  // Slide 6: S – Sensing & Metabolic Fuel-Gauging
  {
    id: 6,
    title: "S: Sensing & Metabolic Fuel-Gauging",
    content: (
      <>
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-blue-800 mb-1">Cellular Pathways Responding to Nutrient Availability & Energy Status</h3>
          <p className="text-xs text-slate-700">Core pathways fundamental to metabolic health, aging, and longevity</p>
        </div>
        
        <SensingBalanceDiagram />
        
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Growth/Storage Pathways</h4>
            <div className="space-y-2">
              <div className="bg-red-50 p-2 rounded border border-red-200">
                <h5 className="text-xs font-semibold text-red-800">Insulin/IGF-1 Signaling (IIS)</h5>
                <p className="text-xs text-red-700">Growth, metabolism. Downregulation linked to longevity.</p>
              </div>
              
              <div className="bg-red-50 p-2 rounded border border-red-200">
                <h5 className="text-xs font-semibold text-red-800">mTOR</h5>
                <p className="text-xs text-red-700">Nutrient sensor, promotes anabolism. Inhibition linked to lifespan extension.</p>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Longevity Pathways</h4>
            <div className="space-y-2">
              <div className="bg-green-50 p-2 rounded border border-green-200">
                <h5 className="text-xs font-semibold text-green-800">AMPK</h5>
                <p className="text-xs text-green-700">Cellular energy sensor. Activation promotes stress resistance, longevity.</p>
              </div>
              
              <div className="bg-green-50 p-2 rounded border border-green-200">
                <h5 className="text-xs font-semibold text-green-800">Sirtuins (SIRT1-7)</h5>
                <p className="text-xs text-green-700">NAD+-dependent deacetylases. Regulate metabolism, stress resistance. Activated by CR, exercise.</p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 7: S – Sensing: Lifestyle Modulation
  {
    id: 7,
    title: "S: Modulating Sensing Pathways Through Lifestyle",
    content: (
      <>
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <h4 className="text-xs font-semibold text-blue-800 mb-2 flex items-center">
              <span className="mr-1">🍎</span> Nutrition
            </h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li><strong>CR & IF:</strong> ↓IIS/mTOR, ↑AMPK/Sirtuins</li>
              <li><em>Protocols:</em> TRE (16/8), 5:2 diet</li>
              <li><strong>Ketogenic Diets:</strong> ↓IIS, modulates mTOR. BHB signaling</li>
              <li><strong>Omega-3s:</strong> Target Omega-3 Index over 8%</li>
              <li><strong>Meal Timing:</strong> Earlier intake aligns with circadian peaks</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <h4 className="text-xs font-semibold text-green-800 mb-2 flex items-center">
              <span className="mr-1">🏃</span> Exercise
            </h4>
            <ul className="text-xs text-green-700 space-y-1">
              <li><strong>Insulin Sensitivity:</strong> Enhanced via multiple mechanisms</li>
              <li><strong>Resistance Exercise:</strong> Acutely ↑mTOR (muscle)</li>
              <li><strong>Endurance/HIIT:</strong> ↑AMPK, ↑Sirtuins</li>
              <li><strong>Zone 2 Training:</strong> Metabolic optimization</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
            <h4 className="text-xs font-semibold text-purple-800 mb-2 flex items-center">
              <span className="mr-1">🌙</span> Sleep
            </h4>
            <ul className="text-xs text-purple-700 space-y-1">
              <li><strong>Deprivation:</strong> Impairs insulin sensitivity</li>
              <li><strong>Disorders:</strong> Affects Sirtuins, Klotho levels</li>
              <li><strong>Optimization:</strong> Supports metabolic health</li>
            </ul>
          </div>
        </div>
        
        <KeyTakeaway>
          Lifestyle interventions act as powerful modulators of cellular sensing pathways, shifting the balance from growth/storage toward maintenance/longevity programs.
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 8: S – Sensing: Key Interventions & Biomarkers Table
  {
    id: 8,
    title: "S: Sensing - Clinical Levers & Monitoring",
    content: (
      <>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Intervention Category</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Specific Strategy</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Key SHIFT Targets</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Clinical Considerations/Dosing</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Relevant Biomarkers</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-3 py-2 font-semibold" rowSpan={4}>Nutrition</td>
                <td className="px-3 py-2">CR / IF (e.g., TRE 16/8)</td>
                <td className="px-3 py-2">↓IIS, ↓mTOR, ↑AMPK, ↑Sirt</td>
                <td className="px-3 py-2">Gradual start, ensure nutrient adequacy, hydration</td>
                <td className="px-3 py-2">Fasting Glucose/Insulin, HbA1c, HOMA-IR, Lipids</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Ketogenic Diet</td>
                <td className="px-3 py-2">↓IIS, mod mTOR</td>
                <td className="px-3 py-2">&lt;50g CHO/day, monitor ketones (BHB 0.5-3mM)</td>
                <td className="px-3 py-2">Blood Ketones, Lipids, Electrolytes</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Omega-3 PUFAs</td>
                <td className="px-3 py-2">↑Insulin Sens. (indirect)</td>
                <td className="px-3 py-2">1-2g EPA+DHA daily, target Omega-3 Index &gt;8%</td>
                <td className="px-3 py-2">Omega-3 Index, Triglycerides</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Meal Timing</td>
                <td className="px-3 py-2">Optimizes IIS</td>
                <td className="px-3 py-2">Eat earlier, avoid late eating (3h pre-bed)</td>
                <td className="px-3 py-2">CGM data, subjective energy</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold" rowSpan={3}>Exercise</td>
                <td className="px-3 py-2">HIIT</td>
                <td className="px-3 py-2">↑AMPK, ↑Sirtuins</td>
                <td className="px-3 py-2">2-3x/week, e.g., 4-6x30s max effort, 2-4m recovery</td>
                <td className="px-3 py-2">VO2max, HOMA-IR</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Resistance Exercise (RE)</td>
                <td className="px-3 py-2">↑mTOR (acute), ↑Sirtuins</td>
                <td className="px-3 py-2">2-3x/week, 60-80% 1RM, progressive overload</td>
                <td className="px-3 py-2">Muscle strength/mass, HOMA-IR</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Endurance (Zone 2)</td>
                <td className="px-3 py-2">↑AMPK, ↑Sirtuins</td>
                <td className="px-3 py-2">3-5x/week, 30-60+ min, 60-70% HRmax</td>
                <td className="px-3 py-2">VO2max, HOMA-IR</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">Sleep</td>
                <td className="px-3 py-2">Optimized Sleep</td>
                <td className="px-3 py-2">Supports IIS, Sirtuins</td>
                <td className="px-3 py-2">7-9h nightly, consistent schedule, sleep hygiene</td>
                <td className="px-3 py-2">PSG/Actigraphy, HOMA-IR</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    )
  },
  
  // Slide 9: H – Hormesis & the Biphasic Response to Stress
  {
    id: 9,
    title: "H: Hormesis - The Biphasic Response to Stress",
    content: (
      <>
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-blue-800 mb-1">Principle: Mild stressors stimulate adaptive responses that enhance resilience</h3>
          <p className="text-xs text-slate-700">"What doesn't kill you makes you stronger" - when applied correctly</p>
        </div>
        
        <HormeticResponseCurveDiagram />
        
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Hormetic Stressors</h4>
            <ul className="text-xs text-slate-700 space-y-1">
              <li>• <strong>Exercise:</strong> Mechanical & metabolic stress</li>
              <li>• <strong>Fasting/CR:</strong> Nutrient scarcity</li>
              <li>• <strong>Cold/Heat:</strong> Thermal stress</li>
              <li>• <strong>Hypoxia:</strong> Oxygen limitation</li>
              <li>• <strong>Phytochemicals:</strong> Mild toxins (polyphenols)</li>
              <li>• <strong>Radiation:</strong> Low-dose exposure</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Adaptive Benefits</h4>
            <ul className="text-xs text-slate-700 space-y-1">
              <li>• ↑ Antioxidant defenses (Nrf2)</li>
              <li>• ↑ Heat shock proteins</li>
              <li>• ↑ Mitochondrial biogenesis</li>
              <li>• ↑ DNA repair mechanisms</li>
              <li>• ↑ Autophagy & proteostasis</li>
              <li>• ↑ Stress resistance</li>
            </ul>
          </div>
        </div>
        
        <KeyTakeaway>
          The dose makes the medicine: Finding the "sweet spot" where stress activates beneficial adaptations without causing harm is key to hormetic interventions.
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 10: H – Hormesis: Lifestyle Applications
  {
    id: 10,
    title: "H: Hormesis in Clinical Practice",
    content: (
      <>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
            <h4 className="text-xs font-semibold text-orange-800 mb-2">Exercise as Hormesis</h4>
            <ul className="text-xs text-orange-700 space-y-1">
              <li><strong>Acute stress:</strong> ↑ROS, inflammation, muscle damage</li>
              <li><strong>Adaptation:</strong> ↑Antioxidants, mitochondria, strength</li>
              <li><strong>Key:</strong> Progressive overload with recovery</li>
              <li><strong>Caution:</strong> Overtraining = harmful stress</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <h4 className="text-xs font-semibold text-blue-800 mb-2">Thermal Hormesis</h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li><strong>Heat (Sauna):</strong> ↑HSPs, ↑BDNF, ↑GH</li>
              <li>• Protocol: 80-100°C, 20-30min, 3-4x/week</li>
              <li><strong>Cold:</strong> ↑Brown fat, ↑NE, ↑resilience</li>
              <li>• Protocol: 11min/week total @ 10-15°C</li>
            </ul>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <h4 className="text-xs font-semibold text-green-800 mb-2">Dietary Hormesis</h4>
            <ul className="text-xs text-green-700 space-y-1">
              <li><strong>Polyphenols:</strong> Mild pro-oxidants → antioxidant response</li>
              <li>• Resveratrol, curcumin, EGCG, quercetin</li>
              <li><strong>Sulforaphane:</strong> Nrf2 activation</li>
              <li>• Source: Cruciferous vegetables</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
            <h4 className="text-xs font-semibold text-purple-800 mb-2">Hypoxic Hormesis</h4>
            <ul className="text-xs text-purple-700 space-y-1">
              <li><strong>IHT:</strong> Intermittent hypoxic training</li>
              <li>• ↑EPO, ↑mitochondria, ↑angiogenesis</li>
              <li><strong>Breath work:</strong> Controlled hypoxic stress</li>
              <li>• Wim Hof method, pranayama</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="text-xs font-semibold text-yellow-800 mb-1">Clinical Considerations</h4>
          <ul className="text-xs text-yellow-700 space-y-0.5">
            <li>• Individual variation in optimal dose</li>
            <li>• Start low, progress gradually</li>
            <li>• Monitor for signs of excessive stress</li>
            <li>• Allow adequate recovery between exposures</li>
          </ul>
        </div>
      </>
    )
  },
  
  // Slide 11: H – Hormesis: Clinical Application Guide
  {
    id: 11,
    title: "H: Hormesis - Practical Implementation",
    content: (
      <>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Hormetic Stressor</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Beginner Protocol</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Advanced Protocol</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Key Adaptations</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Contraindications</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-3 py-2 font-semibold">Exercise (HIIT)</td>
                <td className="px-3 py-2">2x/week, 4x30s intervals</td>
                <td className="px-3 py-2">3-4x/week, 6-8x30s intervals</td>
                <td className="px-3 py-2">↑VO2max, ↑mitochondria</td>
                <td className="px-3 py-2">Unstable CVD, acute injury</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">Heat (Sauna)</td>
                <td className="px-3 py-2">70-80°C, 10-15min, 2x/week</td>
                <td className="px-3 py-2">80-100°C, 20-30min, 4-7x/week</td>
                <td className="px-3 py-2">↑HSPs, ↑BDNF, ↑CVD health</td>
                <td className="px-3 py-2">Pregnancy, unstable angina</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">Cold Exposure</td>
                <td className="px-3 py-2">15°C, 2-3min, 2x/week</td>
                <td className="px-3 py-2">10°C, 5-6min, 4-5x/week</td>
                <td className="px-3 py-2">↑Brown fat, ↑resilience</td>
                <td className="px-3 py-2">Raynaud's, cold urticaria</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">Fasting</td>
                <td className="px-3 py-2">12-14h overnight fast</td>
                <td className="px-3 py-2">16-18h IF or 24h 1x/week</td>
                <td className="px-3 py-2">↑Autophagy, ↑ketones</td>
                <td className="px-3 py-2">T1DM, pregnancy, ED history</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">Phytochemicals</td>
                <td className="px-3 py-2">Diverse plant foods daily</td>
                <td className="px-3 py-2">Targeted supplements (curcumin, etc)</td>
                <td className="px-3 py-2">↑Nrf2, ↑antioxidants</td>
                <td className="px-3 py-2">Medication interactions</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <h4 className="text-xs font-semibold text-blue-800 mb-1">Recovery is Essential</h4>
            <p className="text-xs text-blue-700">Adaptation occurs during recovery. Without adequate rest, hormetic stress becomes harmful chronic stress.</p>
          </div>
          
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <h4 className="text-xs font-semibold text-green-800 mb-1">Personalization is Key</h4>
            <p className="text-xs text-green-700">Optimal dose varies by genetics, fitness level, stress load, and health status. Monitor and adjust.</p>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 12: I – Inflammation: Understanding "Inflammaging"
  {
    id: 12,
    title: "I: Inflammation - The Silent Driver of Aging",
    content: (
      <>
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-blue-800 mb-1">"Inflammaging": Chronic Low-Grade Inflammation with Age</h3>
          <p className="text-xs text-slate-700">Key driver of age-related diseases and functional decline</p>
        </div>
        
        <InflammationPathwayDiagram />
        
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Key Inflammatory Markers</h4>
            <ul className="text-xs text-slate-700 space-y-1">
              <li>• <strong>CRP:</strong> &gt; 3 mg/L = high risk</li>
              <li>• <strong>IL-6:</strong> Primary inflammaging cytokine</li>
              <li>• <strong>TNF-α:</strong> Muscle wasting, insulin resistance</li>
              <li>• <strong>IL-1β:</strong> Neuroinflammation</li>
              <li>• <strong>NF-κB:</strong> Master inflammatory regulator</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Sources of Inflammation</h4>
            <ul className="text-xs text-slate-700 space-y-1">
              <li>• Visceral adiposity (adipokines)</li>
              <li>• Gut dysbiosis (LPS)</li>
              <li>• Cellular senescence (SASP)</li>
              <li>• Mitochondrial dysfunction</li>
              <li>• Environmental toxins</li>
              <li>• Chronic infections</li>
            </ul>
          </div>
        </div>
        
        <KeyTakeaway>
          Chronic inflammation accelerates biological aging and underlies most age-related diseases. Lifestyle interventions can powerfully modulate inflammatory pathways.
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 13: I – Inflammation: Anti-Inflammatory Lifestyle
  {
    id: 13,
    title: "I: Anti-Inflammatory Lifestyle Strategies",
    content: (
      <>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <h4 className="text-xs font-semibold text-green-800 mb-2 flex items-center">
              <span className="mr-1">🥗</span> Anti-Inflammatory Diet
            </h4>
            <ul className="text-xs text-green-700 space-y-1">
              <li><strong>Mediterranean pattern:</strong> ↓CRP 20-40%</li>
              <li><strong>Omega-3s:</strong> EPA/DHA 2-4g/day</li>
              <li><strong>Polyphenols:</strong> EVOO, berries, green tea</li>
              <li><strong>Fiber:</strong> 25-35g/day, diverse sources</li>
              <li><strong>Avoid:</strong> Ultra-processed, high sugar</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <h4 className="text-xs font-semibold text-blue-800 mb-2 flex items-center">
              <span className="mr-1">🏃</span> Exercise Effects
            </h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li><strong>Acute:</strong> Transient ↑ inflammation</li>
              <li><strong>Chronic:</strong> ↓ Baseline inflammation</li>
              <li><strong>Mechanism:</strong> ↑ Anti-inflammatory myokines</li>
              <li><strong>Optimal:</strong> 150-300 min/week moderate</li>
              <li><strong>+ Resistance:</strong> 2-3x/week</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
            <h4 className="text-xs font-semibold text-purple-800 mb-2 flex items-center">
              <span className="mr-1">😴</span> Sleep & Stress
            </h4>
            <ul className="text-xs text-purple-700 space-y-1">
              <li><strong>Sleep debt:</strong> ↑ IL-6, TNF-α</li>
              <li><strong>Target:</strong> 7-9 hrs/night</li>
              <li><strong>Stress:</strong> ↑ Cortisol → ↑ inflammation</li>
              <li><strong>Mind-body:</strong> Meditation ↓ NF-κB</li>
              <li><strong>Social:</strong> Connection ↓ inflammation</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <h4 className="text-xs font-semibold text-yellow-800 mb-2">Targeted Supplements</h4>
            <ul className="text-xs text-yellow-700 space-y-0.5">
              <li>• <strong>Curcumin:</strong> 500-1000mg w/ piperine</li>
              <li>• <strong>Quercetin:</strong> 500-1000mg/day</li>
              <li>• <strong>Resveratrol:</strong> 150-500mg/day</li>
              <li>• <strong>Vitamin D:</strong> Target 40-60 ng/mL</li>
            </ul>
          </div>
          
          <div className="bg-red-50 p-3 rounded-lg border border-red-200">
            <h4 className="text-xs font-semibold text-red-800 mb-2">Weight Management</h4>
            <ul className="text-xs text-red-700 space-y-0.5">
              <li>• Visceral fat = inflammatory organ</li>
              <li>• 5-10% weight loss → ↓ CRP 30-50%</li>
              <li>• Focus on waist circumference</li>
              <li>• Combine diet + exercise</li>
            </ul>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 14: I – Inflammation: Clinical Monitoring & Targets
  {
    id: 14,
    title: "I: Clinical Assessment & Monitoring",
    content: (
      <>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Biomarker</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Optimal Range</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Clinical Utility</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Intervention Response</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-3 py-2 font-semibold">hs-CRP</td>
                <td className="px-3 py-2">&lt; 1.0 mg/L</td>
                <td className="px-3 py-2">CVD risk, systemic inflammation</td>
                <td className="px-3 py-2">↓ 20-50% with lifestyle changes</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">IL-6</td>
                <td className="px-3 py-2">&lt; 1.5 pg/mL</td>
                <td className="px-3 py-2">Inflammaging marker</td>
                <td className="px-3 py-2">↓ with exercise, omega-3s</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">Ferritin</td>
                <td className="px-3 py-2">50-150 ng/mL</td>
                <td className="px-3 py-2">Iron stores + inflammation</td>
                <td className="px-3 py-2">Monitor with interventions</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">Fibrinogen</td>
                <td className="px-3 py-2">200-300 mg/dL</td>
                <td className="px-3 py-2">Clotting + inflammation</td>
                <td className="px-3 py-2">↓ with exercise, weight loss</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">ESR</td>
                <td className="px-3 py-2">Age/2 (men), (Age+10)/2 (women)</td>
                <td className="px-3 py-2">Non-specific inflammation</td>
                <td className="px-3 py-2">Slow to change</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">WBC</td>
                <td className="px-3 py-2">4.5-6.0 x10³/μL</td>
                <td className="px-3 py-2">Immune activation</td>
                <td className="px-3 py-2">↓ with lifestyle changes</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 grid md:grid-cols-3 gap-3">
          <div className="bg-blue-50 p-2 rounded-lg border border-blue-200">
            <h4 className="text-xs font-semibold text-blue-800 mb-1">Testing Frequency</h4>
            <p className="text-xs text-blue-700">Baseline → 3 months → 6 months → annually</p>
          </div>
          
          <div className="bg-green-50 p-2 rounded-lg border border-green-200">
            <h4 className="text-xs font-semibold text-green-800 mb-1">Response Timeline</h4>
            <p className="text-xs text-green-700">CRP: 2-4 weeks; IL-6: 4-8 weeks</p>
          </div>
          
          <div className="bg-purple-50 p-2 rounded-lg border border-purple-200">
            <h4 className="text-xs font-semibold text-purple-800 mb-1">Clinical Pearl</h4>
            <p className="text-xs text-purple-700">Check 2x if elevated; rule out acute illness</p>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 15: F – Fidelity: Genomic & Proteomic Maintenance
  {
    id: 15,
    title: "F: Fidelity - Maintaining Information Integrity",
    content: (
      <>
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-blue-800 mb-1">Preserving Genomic & Proteomic Fidelity Throughout Life</h3>
          <p className="text-xs text-slate-700">DNA repair, proteostasis, and cellular quality control</p>
        </div>
        
        <GenomicMaintenanceDiagram />
        
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Genomic Fidelity</h4>
            <ul className="text-xs text-slate-700 space-y-1">
              <li>• <strong>DNA damage:</strong> ~70,000 lesions/cell/day</li>
              <li>• <strong>Repair pathways:</strong> BER, NER, MMR, NHEJ, HR</li>
              <li>• <strong>Telomeres:</strong> Protective caps, shorten with age</li>
              <li>• <strong>Epigenetic drift:</strong> Age-related changes</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Proteomic Fidelity</h4>
            <ul className="text-xs text-slate-700 space-y-1">
              <li>• <strong>Proteostasis network:</strong> HSPs, UPS, autophagy</li>
              <li>• <strong>Protein misfolding:</strong> Increases with age</li>
              <li>• <strong>Aggregation:</strong> Hallmark of neurodegeneration</li>
              <li>• <strong>Clearance:</strong> Declines with age</li>
            </ul>
          </div>
        </div>
        
        <KeyTakeaway>
          Maintaining information fidelity at both genomic and proteomic levels is critical for healthy aging. Lifestyle factors powerfully influence these quality control systems.
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 16: F – Fidelity: Lifestyle Enhancement Strategies
  {
    id: 16,
    title: "F: Enhancing Fidelity Through Lifestyle",
    content: (
      <>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <h4 className="text-xs font-semibold text-blue-800 mb-2">DNA Repair Enhancement</h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li><strong>Antioxidants:</strong> ↓ Oxidative DNA damage</li>
              <li>• Vitamin C, E, selenium</li>
              <li><strong>Polyphenols:</strong> ↑ Repair enzyme activity</li>
              <li>• Resveratrol, curcumin</li>
              <li><strong>NAD+ precursors:</strong> Support PARP activity</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <h4 className="text-xs font-semibold text-green-800 mb-2">Proteostasis Support</h4>
            <ul className="text-xs text-green-700 space-y-1">
              <li><strong>Heat stress:</strong> ↑ HSP expression</li>
              <li>• Sauna, hot baths</li>
              <li><strong>Exercise:</strong> ↑ Protein quality control</li>
              <li><strong>Fasting:</strong> ↑ Autophagy</li>
              <li><strong>Sleep:</strong> Protein clearance (glymphatic)</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
            <h4 className="text-xs font-semibold text-purple-800 mb-2">Telomere Protection</h4>
            <ul className="text-xs text-purple-700 space-y-1">
              <li><strong>Exercise:</strong> ↑ Telomerase activity</li>
              <li><strong>Meditation:</strong> ↓ Telomere attrition</li>
              <li><strong>Omega-3s:</strong> Associated w/ longer telomeres</li>
              <li><strong>Stress management:</strong> Critical</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="text-xs font-semibold text-yellow-800 mb-2">Avoiding Fidelity Disruptors</h4>
          <div className="grid md:grid-cols-3 gap-2">
            <ul className="text-xs text-yellow-700 space-y-0.5">
              <li>• Smoking (DNA damage)</li>
              <li>• Excess alcohol</li>
              <li>• UV overexposure</li>
            </ul>
            <ul className="text-xs text-yellow-700 space-y-0.5">
              <li>• Processed meats</li>
              <li>• Chronic stress</li>
              <li>• Sleep deprivation</li>
            </ul>
            <ul className="text-xs text-yellow-700 space-y-0.5">
              <li>• Environmental toxins</li>
              <li>• Sedentary behavior</li>
              <li>• High sugar intake</li>
            </ul>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 17: F – Fidelity: Clinical Assessment & Biomarkers
  {
    id: 17,
    title: "F: Clinical Monitoring of Fidelity Systems",
    content: (
      <>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Biomarker Category</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Specific Tests</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Clinical Utility</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Intervention Impact</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-3 py-2 font-semibold" rowSpan={2}>DNA Damage/Repair</td>
                <td className="px-3 py-2">8-OHdG (urine)</td>
                <td className="px-3 py-2">Oxidative DNA damage</td>
                <td className="px-3 py-2">↓ with antioxidants, exercise</td>
              </tr>
              <tr>
                <td className="px-3 py-2">γ-H2AX (blood)</td>
                <td className="px-3 py-2">DNA double-strand breaks</td>
                <td className="px-3 py-2">Research use primarily</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold" rowSpan={2}>Telomere Biology</td>
                <td className="px-3 py-2">Telomere length</td>
                <td className="px-3 py-2">Biological aging marker</td>
                <td className="px-3 py-2">Modest changes with lifestyle</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Telomerase activity</td>
                <td className="px-3 py-2">Regenerative capacity</td>
                <td className="px-3 py-2">↑ with exercise, meditation</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold" rowSpan={2}>Proteostasis</td>
                <td className="px-3 py-2">Heat shock proteins</td>
                <td className="px-3 py-2">Stress response capacity</td>
                <td className="px-3 py-2">↑ with heat therapy, exercise</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Protein carbonyls</td>
                <td className="px-3 py-2">Oxidative protein damage</td>
                <td className="px-3 py-2">↓ with CR, antioxidants</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">Epigenetic</td>
                <td className="px-3 py-2">DNA methylation age</td>
                <td className="px-3 py-2">Biological age assessment</td>
                <td className="px-3 py-2">Responsive to lifestyle</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 bg-blue-50 p-3 rounded-lg border border-blue-200">
          <h4 className="text-xs font-semibold text-blue-800 mb-1">Clinical Pearls</h4>
          <ul className="text-xs text-blue-700 space-y-0.5">
            <li>• Many fidelity biomarkers are research tools; clinical utility varies</li>
            <li>• Focus on modifiable lifestyle factors with proven impact</li>
            <li>• Consider methylation age testing for motivated patients</li>
            <li>• Emphasize prevention over correction</li>
          </ul>
        </div>
      </>
    )
  },
  
  // Slide 18: T – Transport: Optimizing Circulation & Detoxification
  {
    id: 18,
    title: "T: Transport - Circulation & Clearance Systems",
    content: (
      <>
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-blue-800 mb-1">Optimizing Transport: Blood Flow, Lymphatics & Detoxification</h3>
          <p className="text-xs text-slate-700">Supporting the body's delivery and waste removal systems</p>
        </div>
        
        <CirculatoryHealthDiagram />
        
        <div className="grid md:grid-cols-3 gap-3 mt-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Cardiovascular Transport</h4>
            <ul className="text-xs text-slate-700 space-y-1">
              <li>• <strong>Endothelial function</strong></li>
              <li>• <strong>Blood pressure regulation</strong></li>
              <li>• <strong>Lipid transport</strong></li>
              <li>• <strong>Oxygen delivery</strong></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Lymphatic System</h4>
            <ul className="text-xs text-slate-700 space-y-1">
              <li>• <strong>Immune cell transport</strong></li>
              <li>• <strong>Protein clearance</strong></li>
              <li>• <strong>Fluid balance</strong></li>
              <li>• <strong>Fat absorption</strong></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Detoxification</h4>
            <ul className="text-xs text-slate-700 space-y-1">
              <li>• <strong>Phase I: Cytochrome P450</strong></li>
              <li>• <strong>Phase II: Conjugation</strong></li>
              <li>• <strong>Phase III: Elimination</strong></li>
              <li>• <strong>Renal & hepatic clearance</strong></li>
            </ul>
          </div>
        </div>
        
        <KeyTakeaway>
          Efficient transport systems are essential for nutrient delivery, waste removal, and immune function. Lifestyle interventions can significantly enhance all three domains.
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 19: T – Transport: Enhancement Strategies
  {
    id: 19,
    title: "T: Lifestyle Strategies for Optimal Transport",
    content: (
      <>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="bg-red-50 p-3 rounded-lg border border-red-200">
            <h4 className="text-xs font-semibold text-red-800 mb-2">Cardiovascular Enhancement</h4>
            <ul className="text-xs text-red-700 space-y-1">
              <li><strong>Aerobic exercise:</strong> 150+ min/week moderate</li>
              <li><strong>HIIT:</strong> 2-3x/week for VO₂max</li>
              <li><strong>Zone 2 training:</strong> Mitochondrial efficiency</li>
              <li><strong>Beetroot/nitrates:</strong> ↑ NO production</li>
              <li><strong>Omega-3s:</strong> Endothelial function</li>
              <li><strong>Polyphenols:</strong> Vascular health</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <h4 className="text-xs font-semibold text-blue-800 mb-2">Lymphatic Support</h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li><strong>Movement:</strong> Primary lymph driver</li>
              <li><strong>Deep breathing:</strong> Thoracic pump</li>
              <li><strong>Hydration:</strong> Optimal fluid balance</li>
              <li><strong>Dry brushing:</strong> Superficial drainage</li>
              <li><strong>Compression:</strong> During/after exercise</li>
              <li><strong>Inversions:</strong> Gravity assistance</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-3 bg-green-50 p-3 rounded-lg border border-green-200">
          <h4 className="text-xs font-semibold text-green-800 mb-2">Detoxification Support</h4>
          <div className="grid md:grid-cols-3 gap-2">
            <div>
              <h5 className="text-xs font-semibold text-green-700">Phase I Support</h5>
              <ul className="text-xs text-green-600 space-y-0.5">
                <li>• B vitamins</li>
                <li>• Antioxidants</li>
                <li>• Cruciferous vegetables</li>
              </ul>
            </div>
            <div>
              <h5 className="text-xs font-semibold text-green-700">Phase II Support</h5>
              <ul className="text-xs text-green-600 space-y-0.5">
                <li>• Sulfur compounds</li>
                <li>• Amino acids</li>
                <li>• Glutathione precursors</li>
              </ul>
            </div>
            <div>
              <h5 className="text-xs font-semibold text-green-700">Phase III Support</h5>
              <ul className="text-xs text-green-600 space-y-0.5">
                <li>• Fiber intake</li>
                <li>• Hydration</li>
                <li>• Regular elimination</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <h4 className="text-xs font-semibold text-purple-800 mb-1">Special Considerations</h4>
          <ul className="text-xs text-purple-700 space-y-0.5">
            <li>• <strong>Sauna:</strong> Enhances circulation, detox through sweat</li>
            <li>• <strong>Cold exposure:</strong> Vascular training, lymphatic flow</li>
            <li>• <strong>Fasting:</strong> Autophagy, cellular cleanup</li>
            <li>• <strong>Sleep:</strong> Glymphatic system activation</li>
          </ul>
        </div>
      </>
    )
  },
  
  // Slide 20: T – Transport: Clinical Assessment
  {
    id: 20,
    title: "T: Clinical Monitoring of Transport Systems",
    content: (
      <>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-900">System</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Key Biomarkers</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Optimal Targets</th>
                <th className="px-3 py-2 text-left font-medium text-gray-900">Intervention Focus</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-3 py-2 font-semibold" rowSpan={4}>Cardiovascular</td>
                <td className="px-3 py-2">Blood pressure</td>
                <td className="px-3 py-2">&lt;120/80 mmHg</td>
                <td className="px-3 py-2">Exercise, diet, stress mgmt</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Endothelial function (FMD)</td>
                <td className="px-3 py-2">&gt;7% dilation</td>
                <td className="px-3 py-2">Nitric oxide support</td>
              </tr>
              <tr>
                <td className="px-3 py-2">VO₂max</td>
                <td className="px-3 py-2">Age/sex specific</td>
                <td className="px-3 py-2">Aerobic + HIIT training</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Heart rate variability</td>
                <td className="px-3 py-2">Higher = better</td>
                <td className="px-3 py-2">Stress reduction, recovery</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold" rowSpan={3}>Lymphatic</td>
                <td className="px-3 py-2">Clinical assessment</td>
                <td className="px-3 py-2">No edema</td>
                <td className="px-3 py-2">Movement, compression</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Albumin</td>
                <td className="px-3 py-2">3.5-5.0 g/dL</td>
                <td className="px-3 py-2">Protein status</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Immune markers</td>
                <td className="px-3 py-2">WBC differential</td>
                <td className="px-3 py-2">Immune support</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold" rowSpan={4}>Detoxification</td>
                <td className="px-3 py-2">Liver enzymes</td>
                <td className="px-3 py-2">AST/ALT &lt;40 U/L</td>
                <td className="px-3 py-2">Liver support nutrients</td>
              </tr>
              <tr>
                <td className="px-3 py-2">GGT</td>
                <td className="px-3 py-2">&lt;30 U/L</td>
                <td className="px-3 py-2">Oxidative stress marker</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Creatinine/eGFR</td>
                <td className="px-3 py-2">eGFR &gt;90</td>
                <td className="px-3 py-2">Kidney function</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Organic acids (urine)</td>
                <td className="px-3 py-2">Pattern analysis</td>
                <td className="px-3 py-2">Metabolic function</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <KeyTakeaway>
          Regular monitoring of transport systems helps identify areas for targeted intervention. Focus on achievable lifestyle modifications with measurable outcomes.
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 21: Integration - Bringing It All Together
  {
    id: 21,
    title: "Integration: The SHIFT Synergy",
    content: (
      <>
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-blue-800 mb-1">Synergistic Effects Across SHIFT Domains</h3>
          <p className="text-xs text-slate-700">How each pillar reinforces and amplifies the others</p>
        </div>
        
        <div className="space-y-3">
          {/* Exercise Example */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-lg border border-blue-200">
            <h4 className="text-xs font-semibold text-blue-800 mb-2">Example: Exercise Integration</h4>
            <div className="grid md:grid-cols-5 gap-2 text-xs">
              <div className="text-center">
                <div className="font-semibold text-blue-700">S</div>
                <div className="text-blue-600">↑ Insulin sensitivity</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-green-700">H</div>
                <div className="text-green-600">Hormetic stress</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-red-700">I</div>
                <div className="text-red-600">↓ Inflammation</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-purple-700">F</div>
                <div className="text-purple-600">↑ DNA repair</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-amber-700">T</div>
                <div className="text-amber-600">↑ Circulation</div>
              </div>
            </div>
          </div>
          
          {/* Fasting Example */}
          <div className="bg-gradient-to-r from-green-50 to-amber-50 p-3 rounded-lg border border-green-200">
            <h4 className="text-xs font-semibold text-green-800 mb-2">Example: Intermittent Fasting</h4>
            <div className="grid md:grid-cols-5 gap-2 text-xs">
              <div className="text-center">
                <div className="font-semibold text-blue-700">S</div>
                <div className="text-blue-600">Metabolic switching</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-green-700">H</div>
                <div className="text-green-600">↑ Stress resilience</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-red-700">I</div>
                <div className="text-red-600">↓ CRP, IL-6</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-purple-700">F</div>
                <div className="text-purple-600">↑ Autophagy</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-amber-700">T</div>
                <div className="text-amber-600">Enhanced detox</div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-300">
          <h4 className="text-xs font-semibold text-slate-800 mb-2">Key Integration Principles</h4>
          <ul className="text-xs text-slate-700 space-y-1">
            <li>• <strong>Start small:</strong> Pick 1-2 interventions per domain</li>
            <li>• <strong>Stack habits:</strong> Link new behaviors to existing routines</li>
            <li>• <strong>Monitor progress:</strong> Use biomarkers to guide optimization</li>
            <li>• <strong>Personalize:</strong> Adapt to individual needs and responses</li>
            <li>• <strong>Be consistent:</strong> Daily actions compound over time</li>
          </ul>
        </div>
        
        <KeyTakeaway>
          The true power of SHIFT lies in the synergistic interactions between domains. Each healthy behavior creates positive ripple effects across all systems.
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 22: Clinical Implementation
  {
    id: 22,
    title: "Clinical Implementation Strategy",
    content: (
      <>
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-blue-800 mb-1">Implementing SHIFT in Clinical Practice</h3>
          <p className="text-xs text-slate-700">A systematic approach to lifestyle medicine</p>
        </div>
        
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-3">
          <h4 className="text-xs font-semibold text-blue-800 mb-2">The 4-Phase Implementation Process</h4>
          
          <div className="space-y-2">
            <div className="bg-white p-2 rounded border border-blue-300">
              <h5 className="text-xs font-semibold text-blue-700">Phase 1: Assessment (Weeks 1-2)</h5>
              <ul className="text-xs text-blue-600 ml-4 mt-1">
                <li>• Comprehensive lifestyle evaluation</li>
                <li>• Baseline biomarker testing</li>
                <li>• Identify priority areas</li>
              </ul>
            </div>
            
            <div className="bg-white p-2 rounded border border-green-300">
              <h5 className="text-xs font-semibold text-green-700">Phase 2: Foundation (Weeks 3-6)</h5>
              <ul className="text-xs text-green-600 ml-4 mt-1">
                <li>• Sleep optimization</li>
                <li>• Basic movement routine</li>
                <li>• Nutrition fundamentals</li>
              </ul>
            </div>
            
            <div className="bg-white p-2 rounded border border-purple-300">
              <h5 className="text-xs font-semibold text-purple-700">Phase 3: Enhancement (Weeks 7-12)</h5>
              <ul className="text-xs text-purple-600 ml-4 mt-1">
                <li>• Add hormetic stressors</li>
                <li>• Optimize each SHIFT domain</li>
                <li>• Track progress markers</li>
              </ul>
            </div>
            
            <div className="bg-white p-2 rounded border border-amber-300">
              <h5 className="text-xs font-semibold text-amber-700">Phase 4: Maintenance (Ongoing)</h5>
              <ul className="text-xs text-amber-600 ml-4 mt-1">
                <li>• Quarterly check-ins</li>
                <li>• Annual biomarker review</li>
                <li>• Continuous optimization</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-3">
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <h4 className="text-xs font-semibold text-green-800 mb-1">Success Factors</h4>
            <ul className="text-xs text-green-700 space-y-0.5">
              <li>• Patient-centered goals</li>
              <li>• Regular accountability</li>
              <li>• Celebrate small wins</li>
              <li>• Address barriers proactively</li>
            </ul>
          </div>
          
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <h4 className="text-xs font-semibold text-red-800 mb-1">Common Pitfalls</h4>
            <ul className="text-xs text-red-700 space-y-0.5">
              <li>• Too many changes at once</li>
              <li>• Perfectionism over progress</li>
              <li>• Ignoring individual context</li>
              <li>• Insufficient follow-up</li>
            </ul>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 23: Summary & Resources
  {
    id: 23,
    title: "Summary: Lifestyle as Medicine",
    content: (
      <>
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-blue-800 mb-1">The SHIFT Framework: Your Blueprint for Longevity</h3>
          <p className="text-xs text-slate-700">Empowering patients through evidence-based lifestyle interventions</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-green-50 p-4 rounded-lg border border-blue-200 mb-4">
          <h4 className="text-sm font-bold text-center text-slate-800 mb-3">SHIFT: The Five Pillars</h4>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className="font-bold text-blue-700">S</span>
              <span className="text-xs text-slate-700">- <strong>Sensing:</strong> Metabolic flexibility & nutrient signaling</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-bold text-green-700">H</span>
              <span className="text-xs text-slate-700">- <strong>Hormesis:</strong> Beneficial stress & resilience building</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-bold text-red-700">I</span>
              <span className="text-xs text-slate-700">- <strong>Inflammation:</strong> Managing inflammaging</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-bold text-purple-700">F</span>
              <span className="text-xs text-slate-700">- <strong>Fidelity:</strong> Genomic & proteomic maintenance</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-bold text-amber-700">T</span>
              <span className="text-xs text-slate-700">- <strong>Transport:</strong> Circulation, lymphatics & detox</span>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-xs font-semibold text-blue-800 mb-2">Key Takeaways</h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Lifestyle is the most powerful medicine</li>
              <li>• Small changes compound over time</li>
              <li>• Personalization is essential</li>
              <li>• Integration &gt; isolation</li>
              <li>• Consistency beats perfection</li>
            </ul>
          </div>
          
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <h4 className="text-xs font-semibold text-green-800 mb-2">Action Items</h4>
            <ul className="text-xs text-green-700 space-y-1">
              <li>• Assess your current patients</li>
              <li>• Start with 1-2 interventions</li>
              <li>• Track relevant biomarkers</li>
              <li>• Build your toolkit gradually</li>
              <li>• Share success stories</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-slate-100 p-3 rounded-lg border border-slate-300">
          <p className="text-xs text-slate-700 italic text-center">
            "The best time to plant a tree was 20 years ago. The second best time is now."
          </p>
          <p className="text-xs text-slate-600 text-center mt-2">
            Start implementing SHIFT today - your patients' future selves will thank you.
          </p>
        </div>
      </>
    )
  }
];

const LifestyleFoundations: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const presentationRef = useRef<HTMLDivElement>(null);
  const [location, setLocation] = useLocation();

  const handleFullscreen = () => {
    if (!isFullscreen) {
      presentationRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  const goToSlide = (index: number) => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
    }
  };

  const nextSlide = () => {
    goToSlide(currentSlide + 1);
  };

  const previousSlide = () => {
    goToSlide(currentSlide - 1);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === ' ') {
      nextSlide();
    } else if (e.key === 'ArrowLeft') {
      previousSlide();
    } else if (e.key === 'Escape') {
      setIsFullscreen(false);
    }
  };

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);

  return (
    <MentorshipAccessGate>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navbar />
        
        <main className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <Link href="/mentorship-content">
              <Button variant="ghost" className="mb-4">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Mentorship Content
              </Button>
            </Link>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Lifestyle Foundations</h1>
            <p className="text-gray-600">SHIFT Framework Approach to Health Optimization</p>
          </div>

          {/* Presentation Container */}
          <div className="bg-white rounded-lg shadow-lg" ref={presentationRef}>
            {/* Controls */}
            <div className="border-b border-gray-200 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={previousSlide}
                  disabled={currentSlide === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-600 min-w-[100px] text-center">
                  Slide {currentSlide + 1} of {slides.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleFullscreen}
              >
                {isFullscreen ? (
                  <Minimize className="h-4 w-4" />
                ) : (
                  <Maximize className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Slide Content */}
            <div className="p-8" style={{ minHeight: '500px' }}>
              <div className="max-w-4xl mx-auto">
                {slides[currentSlide] && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                      {slides[currentSlide].title}
                    </h2>
                    <div className="prose prose-sm max-w-none">
                      {slides[currentSlide].content}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Slide Navigation Dots */}
            <div className="border-t border-gray-200 p-4">
              <div className="flex justify-center space-x-1">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-blue-600 w-6' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Additional Resources */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-3">Key Learning Objectives</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Understand the SHIFT framework and its application to lifestyle interventions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Learn evidence-based protocols for nutrition, exercise, and sleep optimization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Apply practical strategies for patient care and monitoring</span>
                </li>
              </ul>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-3">Presentation Overview</h3>
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>Duration:</strong> ~30 minutes</p>
                <p><strong>Target Audience:</strong> Physicians new to longevity medicine</p>
                <p><strong>Format:</strong> 23 evidence-based slides with clinical applications</p>
                <p><strong>Focus:</strong> Practical interventions through the SHIFT framework lens</p>
              </div>
            </Card>
          </div>
        </main>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default LifestyleFoundations;
